import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';
import Layout from '../components/Layout';
import { 
  Copy, 
  DollarSign, 
  MousePointer, 
  Users, 
  TrendingUp,
  ExternalLink,
  Calendar,
  CheckCircle,
  Clock,
  ArrowUpRight,
  Banknote
} from 'lucide-react';

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);

  const loadDashboard = useCallback(async () => {
    try {
      const response = await api.get('/affiliate/dashboard');
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Not an affiliate, redirect
        navigate('/affiliate/apply');
      }
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadCommissions = useCallback(async () => {
    try {
      const response = await api.get('/affiliate/commissions');
      setCommissions(response.data.commissions || []);
    } catch (error) {
      console.error('Failed to load commissions:', error);
    }
  }, []);

  const loadReferrals = useCallback(async () => {
    try {
      const response = await api.get('/affiliate/referrals');
      setReferrals(response.data.referrals || []);
    } catch (error) {
      console.error('Failed to load referrals:', error);
    }
  }, []);

  const loadPayouts = useCallback(async () => {
    try {
      const response = await api.get('/affiliate/payouts');
      setPayouts(response.data.payouts || []);
    } catch (error) {
      console.error('Failed to load payouts:', error);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadCommissions();
    loadReferrals();
    loadPayouts();
  }, [loadDashboard, loadCommissions, loadReferrals, loadPayouts]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Referral link copied to clipboard."
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  if (!dashboard) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">Not an Affiliate</h2>
          <p className="text-gray-600 mb-4">You need to apply and be approved as an affiliate first.</p>
          <Button onClick={() => navigate('/affiliate/apply')}>
            Apply Now
          </Button>
        </div>
      </Layout>
    );
  }

  const { affiliate, stats, recent, clicks_by_day } = dashboard;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Affiliate Dashboard</h1>
            <p className="text-gray-600">Welcome back, {affiliate.name}</p>
          </div>
          <Badge variant="outline" className="text-green-600 border-green-600">
            <CheckCircle className="w-3 h-3 mr-1" />
            Active Partner
          </Badge>
        </div>

        {/* Referral Link Card */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Your Referral Link</CardTitle>
            <CardDescription>Share this link to earn 20% lifetime commission</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={affiliate.referral_link}
                readOnly
                className="bg-white font-mono text-sm"
                data-testid="referral-link-input"
              />
              <Button 
                onClick={() => copyToClipboard(affiliate.referral_link)}
                variant="outline"
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-3 flex gap-4 text-sm text-gray-600">
              <span>Referral Code: <code className="bg-white px-2 py-1 rounded font-mono">{affiliate.referral_code}</code></span>
              <span>Commission Rate: <strong className="text-green-600">{(affiliate.commission_rate * 100).toFixed(0)}%</strong></span>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MousePointer className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Clicks</p>
                  <p className="text-2xl font-bold">{stats.total_clicks.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Conversions</p>
                  <p className="text-2xl font-bold">{stats.total_conversions}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Conversion Rate</p>
                  <p className="text-2xl font-bold">{stats.conversion_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Earnings</p>
                  <p className="text-2xl font-bold">${stats.total_earnings.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Earnings Breakdown */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6 text-center">
              <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-green-800">Paid Earnings</p>
              <p className="text-3xl font-bold text-green-700">${stats.paid_earnings.toFixed(2)}</p>
            </CardContent>
          </Card>

          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="pt-6 text-center">
              <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-sm text-yellow-800">Pending Payout</p>
              <p className="text-3xl font-bold text-yellow-700">${stats.pending_earnings.toFixed(2)}</p>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6 text-center">
              <ArrowUpRight className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-blue-800">Last 30 Days</p>
              <p className="text-3xl font-bold text-blue-700">${recent.earnings_30d.toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Details */}
        <Tabs defaultValue="commissions" className="w-full">
          <TabsList>
            <TabsTrigger value="commissions">Commissions</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
            <TabsTrigger value="referrals">Referred Customers</TabsTrigger>
            <TabsTrigger value="activity">Click Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="commissions">
            <Card>
              <CardHeader>
                <CardTitle>Commission History</CardTitle>
                <CardDescription>All your earned commissions</CardDescription>
              </CardHeader>
              <CardContent>
                {commissions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No commissions yet. Share your referral link to start earning!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {commissions.map((commission) => (
                      <div 
                        key={commission.commission_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{commission.customer_email}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(commission.created_at).toLocaleDateString()} • 
                            ${commission.payment_amount.toFixed(2)} payment
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">
                            +${commission.commission_amount.toFixed(2)}
                          </p>
                          <Badge variant={commission.status === 'paid' ? 'default' : 'secondary'}>
                            {commission.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payouts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Banknote className="w-5 h-5" /> Payout History</CardTitle>
                <CardDescription>Record of all payouts you've received</CardDescription>
              </CardHeader>
              <CardContent>
                {payouts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Banknote className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No payouts yet</p>
                    <p className="text-sm">Payouts are processed once you have pending earnings.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payouts.map((payout) => (
                      <div key={payout.payout_id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`payout-${payout.payout_id}`}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <p className="font-semibold text-green-700">${(payout.amount || 0).toFixed(2)}</p>
                            <Badge variant="outline" className="capitalize text-xs">{payout.method?.replace('_', ' ')}</Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {payout.commission_count} commission{payout.commission_count !== 1 ? 's' : ''} 
                            {payout.reference && <> &middot; Ref: {payout.reference}</>}
                          </p>
                        </div>
                        <p className="text-sm text-gray-400">
                          {payout.created_at ? new Date(payout.created_at).toLocaleDateString() : '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>Referred Customers</CardTitle>
                <CardDescription>Customers who signed up through your link</CardDescription>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No referrals yet. Share your link to get started!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((referral) => (
                      <div 
                        key={referral.tenant_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">{referral.name}</p>
                            <p className="text-sm text-gray-500">
                              Joined {new Date(referral.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge>{referral.plan || 'Free'}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Click Activity (Last 30 Days)</CardTitle>
                <CardDescription>Daily clicks on your referral link</CardDescription>
              </CardHeader>
              <CardContent>
                {clicks_by_day.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No clicks recorded yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {clicks_by_day.slice(-14).map((day) => (
                      <div 
                        key={day.date}
                        className="flex items-center gap-3"
                      >
                        <span className="text-sm text-gray-500 w-24">{day.date}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full bg-purple-500 rounded-full"
                            style={{ 
                              width: `${Math.min((day.clicks / Math.max(...clicks_by_day.map(d => d.clicks))) * 100, 100)}%` 
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-12 text-right">{day.clicks}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Help Section */}
        <Card className="bg-gray-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ExternalLink className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Tips for Success</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Share your link on social media, blogs, and newsletters</li>
                  <li>• Write tutorials or reviews about NexusCMS</li>
                  <li>• Include your link in your email signature</li>
                  <li>• Create video content showcasing NexusCMS features</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
